import React from 'react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { toolCallName } from '@/utils/toolCalls';

export interface ToolCallItem {
  name: string;
  args: Record<string, any>;
  status?: 'ok' | 'error' | 'pending';
  duration_ms?: number | null;
}

export const ToolTimeline: React.FC<{ calls: ToolCallItem[] }> = ({ calls }) => {
  const { t } = useTranslation();
  if (!calls || calls.length === 0) return null;
  
  // Deduplicate tool calls: group by name + args signature
  const deduplicatedCalls = React.useMemo(() => {
    const callMap = new Map<string, { call: ToolCallItem; count: number; indices: number[] }>();
    
    calls.forEach((call, idx) => {
      // Create signature from name + args (sorted for consistency)
      const argsStr = JSON.stringify(call.args || {}, Object.keys(call.args || {}).sort());
      const signature = `${toolCallName(call)}:${argsStr}`;
      
      if (callMap.has(signature)) {
        const existing = callMap.get(signature)!;
        existing.count++;
        existing.indices.push(idx);
      } else {
        callMap.set(signature, { call, count: 1, indices: [idx] });
      }
    });
    
    return Array.from(callMap.values());
  }, [calls]);
  
  return (
    <div className="mt-2">
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">{t.chat.toolTimeline}</div>
      <div className="flex flex-wrap items-center gap-2">
        {deduplicatedCalls.map((item, idx) => {
          const c = item.call;
          const isDuplicate = item.count > 1;
          const statusClass = c.status === 'ok'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : c.status === 'error'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
          
          return (
            <div key={idx} className={clsx('px-2 py-1 rounded text-[11px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center space-x-2')}> 
              <span className="font-medium text-gray-700 dark:text-gray-200">{toolCallName(c)}</span>
              {typeof c.duration_ms !== 'undefined' && c.duration_ms !== null && (
                <span className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-[10px] text-gray-700 dark:text-gray-200">{c.duration_ms} ms</span>
              )}
              {c.status && (
                <span className={clsx('px-1.5 py-0.5 rounded text-[10px]', statusClass)}>{c.status}</span>
              )}
              {isDuplicate && (
                <span className="px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-[10px] font-semibold" title={`Called ${item.count} times with identical parameters`}>
                  ×{item.count}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


